import { FormGroup } from '@ksyos/design-system';
import { useEffect } from 'react';
import { getLanguage, getNestedTranslation } from '@common/translate/translate';
import { formatString } from '@common/utils/formatString';
import type { IIdentifierDeclaration } from '../../../common/interfaces/identifierDeclaration';
import type { ValidationErrors } from '../../utils/validation';
import { isDeclarableQualificationNeeded } from '../../utils/isDeclarableQualificationNeeded';
import { InputTriState } from '../elements/common';
import { FormContextProvider } from '../elements/formContext';
import { FormParagraphFormGroup } from '../fields/FormParagraphField';
import { InputTextField } from './InputText';

export interface IIdentifierDeclarationProps {
    dontValidateOnBlur?: boolean;
    errors: ValidationErrors;
    hidePlaceholder?: boolean;
    identifierDeclarationCodeLabel?: string;
    isRequired?: boolean;
    onChange: <K extends keyof IIdentifierDeclaration>(name: K, value: IIdentifierDeclaration[K]) => Promise<void>;
    onValidateOne?: (name: keyof IIdentifierDeclaration) => Promise<void>;
    readOnly?: boolean;
    showHasDeclarableQualificationField?: boolean;
    value: IIdentifierDeclaration;
}

export function IdentifierDeclaration(props: Readonly<IIdentifierDeclarationProps>) {
    const translate = getLanguage();

    useEffect(() => {
        if (props.showHasDeclarableQualificationField) {
            handleHasDeclarableQualification();
        }
    }, [props.value.identifierDeclaration, props.showHasDeclarableQualificationField]);

    return (
        <FormContextProvider
            value={{
                errors: props.errors,
                item: props.value,
                onChange: async (key, value) => handleChange(key, value),
                onDoValidate: (key) => handleValidateOne(key),
                placeholderGroupName: 'identifierDeclaration',
            }}
        >
            {renderIdentifierDeclaration()}
            {props.showHasDeclarableQualificationField && renderHasDeclarableQualification()}
        </FormContextProvider>
    );

    function renderIdentifierDeclaration() {
        if (props.readOnly) {
            return (
                <FormParagraphFormGroup
                    label={
                        props.identifierDeclarationCodeLabel
                            ? getNestedTranslation(props.identifierDeclarationCodeLabel)
                            : translate.practitioner.identifierDeclaration
                    }
                >
                    {props.value.identifierDeclaration ?? '-'}
                </FormParagraphFormGroup>
            );
        }

        return (
            <InputTextField
                dontValidateOnBlur={props.dontValidateOnBlur}
                error={
                    props.errors.get('practitioner.identifierDeclaration') ?? props.errors.get('identifierDeclaration')
                }
                fieldKey={'identifierDeclaration'}
                hasErrors={
                    props.errors.has('practitioner.identifierDeclaration') || props.errors.has('identifierDeclaration')
                }
                isRequired={props.isRequired}
                label={props.identifierDeclarationCodeLabel ?? 'practitioner.identifierDeclaration'}
                nullable={true}
                placeholder={
                    !props.hidePlaceholder
                        ? formatString(translate.personalSettings.addIdentifier, [
                              translate.practitioner.identifierDeclaration,
                          ])
                        : ''
                }
            />
        );
    }

    function renderHasDeclarableQualification() {
        if (!isDeclarableQualificationNeeded(props.value.identifierDeclaration ?? null)) {
            return null;
        }

        const hasDeclarableQualificationString = props.value.hasDeclarableQualification
            ? translate.common.yes
            : translate.common.no;

        if (props.readOnly) {
            return (
                <FormParagraphFormGroup label={translate.practitioner.hasDeclarableQualification}>
                    {hasDeclarableQualificationString || '-'}
                </FormParagraphFormGroup>
            );
        }

        return (
            <FormGroup
                isRequired={props.isRequired}
                label={translate.practitioner.hasDeclarableQualification}
            >
                <InputTriState
                    fieldKey="hasDeclarableQualification"
                    name="hasDeclarableQualification"
                    notNullable={true}
                    onChange={async (value) => await handleChange('hasDeclarableQualification', Boolean(value))}
                    value={props.value.hasDeclarableQualification ?? false}
                />
            </FormGroup>
        );
    }

    async function handleChange<K extends keyof IIdentifierDeclaration>(name: K, value: IIdentifierDeclaration[K]) {
        await props.onChange(name, value);
    }

    async function handleHasDeclarableQualification() {
        // Only AGB starting with 84 should have a hasDeclarableQualification setting
        const declarableQualificationNeeded = isDeclarableQualificationNeeded(props.value.identifierDeclaration);

        if (!declarableQualificationNeeded) {
            handleChange('hasDeclarableQualification', null);
        }
        if (declarableQualificationNeeded && props.value.hasDeclarableQualification === null) {
            handleChange('hasDeclarableQualification', false);
        }
    }

    function handleValidateOne(name: keyof IIdentifierDeclaration) {
        if (name === 'hasDeclarableQualification') {
            // To prevent checking of previous state value in parent
            return;
        }
        if (props.onValidateOne) {
            props.onValidateOne(name);
        }
    }
}
